/**
 * Trip sharing — the pure half.
 *
 * WHAT THIS IS
 * ------------
 * A traveller can invite other people to look at a trip they are planning: an
 * itinerary, or a single booking. Each invitation names one email address and
 * is its own unguessable link, so the owner keeps a real guest list and can
 * revoke one person without disturbing the others.
 *
 * An invited person MUST have a Vhakacha account, and must be signed in with
 * the address they were invited at. That applies identically to itineraries
 * and to bookings — the gate is on the invitation, so neither can be opened by
 * anyone who is not on its guest list.
 *
 * Everything here is pure so it can be tested without Firestore. The writes
 * live in ./tripInvites.js, the same split reviews.js and follows.js use.
 *
 * WHY A PER-PERSON LINK RATHER THAN ONE LINK PER TRIP
 * ---------------------------------------------------
 * One shared link cannot answer "who is on this trip?", which is half of what
 * was asked for, and it cannot be revoked for one person. A link per person
 * gives both, and costs nothing extra — the token is a document id either way.
 *
 * THE TOKEN IS NOT A CREDENTIAL ON ITS OWN.
 * ------------------------------------------
 * An invitation names one email address, and only an account holding that
 * address can open it. The link says WHICH invitation; the signed-in account
 * says WHETHER you may have it. Both are required.
 *
 * That is what makes a forwarded link harmless — the person it is forwarded to
 * cannot read the trip — and it is what finally closes the hole a link-only
 * model could not: a read can now prove who is asking, so access can genuinely
 * be withdrawn rather than merely relabelled.
 *
 * A shared view still exposes a SNAPSHOT rather than the trip document; see
 * buildItinerarySnapshot for what is deliberately left out of it.
 */

import { generateUUID } from './ids';

/**
 * The answer to the quiz's "Who's coming with you?" that means one person.
 *
 * Matched against `itinerary.typeoftravelers`, which stores the ANSWER VALUE.
 * The quiz's other three answers are "Partner", "Family" and "Friends".
 * Spelled with one L because that is what the quiz writes — see
 * src/data/recquizdata.js. Do not "correct" it here without migrating the
 * stored data, or every existing solo trip silently becomes shareable.
 */
export const SOLO_TRAVELLER_ANSWER = 'Traveling Solo';

/** How many people one trip may be shared with. */
export const MAX_COMPANIONS = 20;

/** Matches the review comment cap, for the same reason: a paragraph, not an essay. */
export const MAX_COMMENT_LENGTH = 1000;

/** What an invitee can leave on a trip. */
export const COMMENT_KINDS = ['COMMENT', 'SUGGESTION'];

/**
 * Is this itinerary a solo trip?
 *
 * Unanswered counts as NOT solo. The quiz does not require an answer to any
 * question, so `typeoftravelers` is routinely absent — treating absent as solo
 * would hide the invite button from most trips. The failure modes are not
 * symmetric: offering to share a trip nobody else is on is a no-op the
 * traveller ignores, while hiding the button on a family trip is the feature
 * not existing.
 */
export function isSoloItinerary(itinerary) {
  const answer = itinerary?.typeoftravelers;
  if (typeof answer !== 'string') return false;
  return answer.trim().toLowerCase() === SOLO_TRAVELLER_ANSWER.toLowerCase();
}

/**
 * May this itinerary be shared?
 *
 * Two gates, both required by the brief: it must exist, and it must not be a
 * solo trip.
 */
export function canInviteToItinerary(itinerary) {
  if (!itinerary || !itinerary.id) return false;
  return !isSoloItinerary(itinerary);
}

/**
 * May this booking be shared?
 *
 * A booking carries no "who's coming with you" answer — it was never asked one.
 * The equivalent fact is the party size, which the booking does carry, so a
 * booking for more than one guest is the analogue of a non-solo trip.
 *
 * `guests` is written by buildBooking as adults + children + infants. Falling
 * back to the traveller counts covers legacy documents that predate it.
 */
export function canInviteToBooking(booking) {
  if (!booking || !booking.id) return false;

  const guests = Number(booking.guests);
  if (Number.isFinite(guests) && guests > 0) return guests > 1;

  const t = booking.travelers || {};
  const counted = ['adults', 'children', 'infants']
    .map((k) => Number(t[k]))
    .filter((n) => Number.isFinite(n) && n > 0)
    .reduce((sum, n) => sum + n, 0);

  return counted > 1;
}

/**
 * Why a trip cannot be shared, in words a traveller can act on.
 * Returns null when it can be.
 */
export function whyNotShareable(trip, tripType) {
  if (!trip) return 'This trip has not loaded yet.';

  if (tripType === 'ITINERARY') {
    if (isSoloItinerary(trip)) {
      return 'You told us you are travelling solo, so there is nobody to invite. Change "Who’s coming with you?" to share this trip.';
    }
    return canInviteToItinerary(trip) ? null : 'This trip cannot be shared yet.';
  }

  if (tripType === 'BOOKING') {
    return canInviteToBooking(trip)
      ? null
      : 'This booking is for one guest, so there is nobody to invite. Add a guest to share it.';
  }

  return 'Unknown trip type.';
}

/** A fresh, unguessable invitation token. Doubles as the invite document id. */
export function newInviteToken() {
  return generateUUID();
}

/** A fresh share id. Separate from the invite token so revoking one person does not break the rest. */
export function newShareId() {
  return generateUUID();
}

/**
 * The name shown in the guest list.
 *
 * Required, because a guest list of blank rows is not a guest list. Capped so
 * one long name cannot break the layout of everyone else's row.
 */
export function normaliseInviteName(raw) {
  if (typeof raw !== 'string') return '';
  return raw.trim().replace(/\s+/g, ' ').slice(0, 80);
}

/**
 * The address this invitation belongs to.
 *
 * REQUIRED, and no longer merely a destination for an email. It is now the
 * thing the database checks a reader against, so an invitation without one
 * could never be opened by anybody. Lower-cased because that is the form
 * Firebase puts in the auth token, and a comparison that fails on casing would
 * lock out the very person invited.
 */
export function normaliseInviteEmail(raw) {
  if (typeof raw !== 'string') return '';
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) ? trimmed.slice(0, 254) : '';
}

/** True when the string was given but is not a usable address. */
export function isInvalidEmail(raw) {
  return typeof raw === 'string' && raw.trim() !== '' && normaliseInviteEmail(raw) === '';
}

/**
 * Validates the address an invitation is being sent to.
 * Returns `{ error }` or `{ email }`, the same shape normaliseComment uses.
 */
export function requireInviteEmail(raw) {
  const email = normaliseInviteEmail(raw);
  if (!email) {
    return {
      error: 'Add their email address. They will need to sign in with it to open the trip.',
    };
  }
  return { email };
}

/**
 * Enough of an address for the right person to recognise it, and not enough for
 * anyone else to learn it.
 *
 *   tendai.moyo@example.com  ->  te********@example.com
 *
 * This is shown on the sign-in gate, which by definition renders to someone not
 * yet signed in — so a link that reaches the wrong hands must not hand over a
 * working address along with it. Two characters is enough to recognise your own
 * address and useless for guessing somebody else's.
 */
export function maskEmail(email) {
  if (typeof email !== 'string' || !email.includes('@')) return '';
  const [local, domain] = email.split('@');
  if (!local || !domain) return '';
  // A FIXED number of stars. Mirroring the local part's length leaks it, and
  // knowing an address is 6 characters before the @ is a meaningful head start
  // for anyone guessing at it.
  return `${local.slice(0, 2)}*****@${domain}`;
}

/**
 * Which of the four invitation screens to show.
 *
 * `canRead` is whether the invitation actually came back — the DATABASE decides
 * that, by comparing the caller's address against the one the invitation names.
 * This function does not re-decide it, because a second opinion computed in the
 * browser is one that can disagree with the rules, and the version people would
 * then trust is the wrong one. It only interprets the refusal.
 *
 * The difference between the last two matters: "sign in" is an instruction, and
 * showing it to somebody who IS signed in is a loop they cannot escape. They
 * need to sign out.
 *
 * `emailVerified` earns its own outcome because it is the one refusal a guest
 * can fix without help, and because the alternative — telling somebody signed
 * in at the right address that they are the wrong person — is both false and
 * unactionable.
 *
 * @returns {'ALLOWED'|'SIGN_IN_REQUIRED'|'EMAIL_UNVERIFIED'|'WRONG_ACCOUNT'}
 */
export function inviteGateState({ canRead, userEmail, emailVerified = true }) {
  if (canRead) return 'ALLOWED';
  if (!userEmail) return 'SIGN_IN_REQUIRED';
  if (!emailVerified) return 'EMAIL_UNVERIFIED';
  return 'WRONG_ACCOUNT';
}

/**
 * Turns a comment into what gets stored, or returns a reason it cannot be.
 * Same shape as reviews.js: `{ error }` or the document body.
 */
export function normaliseComment({ body, authorName, kind = 'COMMENT' }) {
  const text = typeof body === 'string' ? body.trim() : '';
  if (!text) return { error: 'Write something before posting.' };

  const name = normaliseInviteName(authorName) || 'A traveller';
  const safeKind = COMMENT_KINDS.includes(kind) ? kind : 'COMMENT';

  return {
    body: text.slice(0, MAX_COMMENT_LENGTH),
    authorName: name,
    kind: safeKind,
  };
}

/**
 * The absolute link handed to an invitee.
 *
 * Built from the running origin rather than a constant. The itinerary
 * confirmation email hardcoded http://localhost:3000 and shipped, which made
 * its only call to action dead for every real recipient; there is no reason to
 * repeat that here. `origin` is injectable so the tests do not need a DOM.
 */
export function inviteLink(token, origin) {
  const base =
    origin ||
    (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  return `${base}/trip/${token}`;
}

/**
 * Dates in the shape the itinerary carries them, flattened to plain millis.
 *
 * A snapshot is read by clients that are not signed in, so it must not contain
 * Firestore Timestamp instances that only the SDK can rehydrate — and must not
 * assume the pair shape, because a Day Activity stores a single timestamp.
 */
export function flattenDates(dates) {
  const toMillis = (value) => {
    if (!value) return null;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  if (Array.isArray(dates)) return dates.map(toMillis).filter((n) => n !== null);
  const single = toMillis(dates);
  return single === null ? [] : [single];
}

/**
 * What an invited person is allowed to see of an itinerary.
 *
 * AN ALLOW-LIST, NOT A DELETE-LIST. Spreading the itinerary and removing a few
 * fields means every field added later is public by default, and the first one
 * that should not have been will not be noticed. Naming what goes in means a
 * new field is private until someone decides otherwise.
 *
 * Left out on purpose:
 *   uid           - the owner's account id, of no use to a viewer
 *   interests     - the owner's stated preferences, not the group's
 *   bookings.accommodation.contact - operator phone/email; a viewer who has not
 *                   booked anything has no business with the operator's direct
 *                   line, and it is one screenshot away from being public
 *   bookings.accommodation.bookingId - the id of a financial record
 */
export function buildItinerarySnapshot(itinerary, schedule = []) {
  if (!itinerary) return null;

  const accommodation = itinerary.bookings?.accommodation || null;

  return {
    tripType: 'ITINERARY',
    tripId: itinerary.id,
    title: itinerary.location ? `Trip to ${itinerary.location}` : 'Trip',
    location: itinerary.location || '',
    vacationtype: itinerary.vacationtype || '',
    typeoftravelers: itinerary.typeoftravelers || '',
    typeofstay: itinerary.typeofstay || '',
    dates: flattenDates(itinerary.dates),
    travelers: {
      adults: Number(itinerary.travelers?.adults) || 0,
      children: Number(itinerary.travelers?.children) || 0,
      infants: Number(itinerary.travelers?.infants) || 0,
    },
    finalized: !!itinerary.finalized,
    accommodation: accommodation
      ? {
          id: accommodation.id || '',
          name: accommodation.name || '',
          type: accommodation.type || '',
          location: accommodation.location || '',
        }
      : null,
    // The day plan, flattened to what a reader needs. The suggestion lists are
    // trimmed to their names: a viewer gets the shape of the day, not a second
    // copy of the catalogue.
    schedule: (Array.isArray(schedule) ? schedule : []).map((day) => ({
      day: day.day,
      slots: (day.schedule || []).map((slot) => ({
        dayTime: slot.dayTime || '',
        time: slot.time || '',
        description: slot.description || '',
        restaurants: (slot.restaurants || []).slice(0, 4).map((r) => r?.name).filter(Boolean),
        activities: (slot.activities || []).slice(0, 4).map((a) => a?.name).filter(Boolean),
      })),
    })),
  };
}

/**
 * What an invited person is allowed to see of a booking.
 *
 * Money is included — a companion being asked to split a bill needs the total.
 * The commission fields are not: they are the platform's arrangement with the
 * operator and appear nowhere in the traveller app either.
 */
export function buildBookingSnapshot(booking) {
  if (!booking) return null;

  return {
    tripType: 'BOOKING',
    tripId: booking.id,
    title: booking.listing?.name || booking.listingName || 'Booking',
    location: booking.listing?.location || booking.location || '',
    listingType: booking.listing?.type || booking.listingType || '',
    dates: flattenDates(booking.dates || [booking.startDate, booking.endDate]),
    nights: Number(booking.nights) || 1,
    travelers: {
      adults: Number(booking.travelers?.adults) || 0,
      children: Number(booking.travelers?.children) || 0,
      infants: Number(booking.travelers?.infants) || 0,
    },
    guests: Number(booking.guests) || 0,
    currency: booking.currency || 'USD',
    totalPaid: Number(booking.totalPaid) || 0,
    status: booking.status || 'PENDING',
    finalized: false,
  };
}

/**
 * One line describing the guest list, for the owner's screen.
 * Counts only invitations still standing.
 */
export function summariseGuests(invites) {
  const live = (Array.isArray(invites) ? invites : []).filter((i) => i && !i.revoked);
  if (!live.length) return 'Nobody invited yet.';
  if (live.length === 1) return '1 person invited.';
  return `${live.length} people invited.`;
}

/** Sort order for a guest list: most recently invited first, revoked at the end. */
export function sortInvites(invites) {
  return [...(Array.isArray(invites) ? invites : [])].sort((a, b) => {
    if (!!a.revoked !== !!b.revoked) return a.revoked ? 1 : -1;
    return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
  });
}
